import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ExecutivePage, PageHero } from "@/components/executive/system";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

type StandardRate = {
  id: string;
  duration_minutes: number;
  rate_cents: number;
  label: string | null;
};

type Override = {
  id: string;
  instructor_id: string;
  class_slug: string | null;
  compensation_source: string;
  rate_cents: number | null;
  rate_type: string | null;
  pay_schedule: string;
  pay_recurrence: string | null;
  pay_cadence_days: number | null;
  next_pay_date: string | null;
  pay_schedule_label: string | null;
  payment_method: string | null;
  notes: string | null;
  effective_start_date: string | null;
  effective_end_date: string | null;
  rate_model?: string | null;
  tier_threshold_students?: number | null;
  tier_increment_cents?: number | null;
};

type Instructor = { id: string; full_name: string; preferred_name: string | null; status: string };

function money(cents: number | null | undefined) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-serif text-foreground mb-3">{title}</h2>
    {children}
  </section>
);

export default function PayrollRulesPage() {
  const [rates, setRates] = useState<StandardRate[]>([]);
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Record<string, string>>({});

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("payroll-engine", {
      body: { action: "list_payroll_rules" },
    });
    if (error) {
      toast.error("Could not load payroll rules");
    } else {
      setRates((data as any).standard_rates ?? []);
      setOverrides((data as any).overrides ?? []);
      setInstructors((data as any).instructors ?? []);
    }
    setLoading(false);
  }

  useEffect(() => { void load(); }, []);

  const nameFor = (id: string) => {
    const i = instructors.find((x) => x.id === id);
    return i ? (i.preferred_name || i.full_name) : id;
  };

  async function saveRate(row: StandardRate) {
    const raw = editing[row.id];
    if (raw == null) return;
    const dollars = parseFloat(raw);
    if (!isFinite(dollars) || dollars < 0) {
      toast.error("Enter a valid dollar amount");
      return;
    }
    const cents = Math.round(dollars * 100);
    const { error } = await supabase
      .from("payroll_standard_rates")
      .update({ rate_cents: cents })
      .eq("id", row.id);
    if (error) toast.error(error.message);
    else {
      toast.success(`Updated ${row.duration_minutes}-min rate`);
      setEditing((s) => { const c = { ...s }; delete c[row.id]; return c; });
      void load();
    }
  }

  const isTiered = (o: Override) => o.rate_model === "enrollment_tiered";
  const tieredOverrides = overrides.filter(isTiered);
  const standardOverrides = overrides.filter((o) => o.pay_schedule === "standard" && !isTiered(o));
  const customOverrides = overrides.filter((o) => o.pay_schedule === "custom");
  const volunteerOverrides = overrides.filter((o) => o.compensation_source === "volunteer");

  return (
    <ExecutivePage section="dashboard" hideDock maxWidthClassName="max-w-4xl">
      <PageHero
        eyebrow="Settings"
        title={<>Payroll Rules</>}
        subtitle={<>Standard rates, overrides, and custom pay schedules for every instructor.</>}
      />

      {loading && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}

      {/* Standard rates */}
      <Section title="Standard Hub Rates">
        <p className="text-sm text-muted-foreground mb-3">
          Fixed pay per meeting for active instructors on the standard schedule.
        </p>
        <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
          {rates.map((r) => {
            const val = editing[r.id];
            const dirty = val != null;
            return (
              <li key={r.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1">
                  <p className="text-[15px] font-medium text-foreground">
                    {r.duration_minutes} minutes
                  </p>
                  <p className="text-xs text-muted-foreground">{r.label ?? ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">$</span>
                  <Input
                    className="w-24 h-9"
                    inputMode="decimal"
                    defaultValue={(r.rate_cents / 100).toFixed(2)}
                    onChange={(e) => setEditing((s) => ({ ...s, [r.id]: e.target.value }))}
                  />
                  <Button size="sm" disabled={!dirty} onClick={() => saveRate(r)}>
                    Save
                  </Button>
                </div>
              </li>
            );
          })}
          {rates.length === 0 && !loading && (
            <li className="px-4 py-6 text-sm text-muted-foreground">No standard rates configured.</li>
          )}
        </ul>
      </Section>

      {/* Custom-schedule overrides */}
      <Section title="Custom Pay Schedules">
        <p className="text-sm text-muted-foreground mb-3">
          Instructors on their own payroll cadence. Their pay appears in a dedicated section on Ops Home.
        </p>
        {customOverrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">None configured.</p>
        ) : (
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {customOverrides.map((o) => (
              <li key={o.id} className="px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-foreground truncate">
                      {nameFor(o.instructor_id)}
                      {o.class_slug && <span className="text-muted-foreground"> · {o.class_slug}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {o.pay_schedule_label ?? "Custom payroll"}
                      {o.pay_recurrence && <> · {o.pay_recurrence}</>}
                      {o.next_pay_date && <> · next {o.next_pay_date}</>}
                      {o.payment_method && <> · {o.payment_method}</>}
                    </p>
                    {o.notes && <p className="text-xs text-muted-foreground italic mt-1">{o.notes}</p>}
                  </div>
                  <span className="text-sm font-medium text-foreground shrink-0">
                    {money(o.rate_cents)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Volunteer overrides */}
      <Section title="Volunteers">
        {volunteerOverrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">None marked as explicit volunteers.</p>
        ) : (
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {volunteerOverrides.map((o) => (
              <li key={o.id} className="px-4 py-3">
                <p className="text-[15px] font-medium text-foreground">
                  {nameFor(o.instructor_id)}
                  {o.class_slug && <span className="text-muted-foreground"> · {o.class_slug}</span>}
                </p>
                {o.notes && <p className="text-xs text-muted-foreground italic mt-1">{o.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Standard-rate overrides */}
      {/* Enrollment-tiered pay (generic: base + per-student increment above a threshold) */}
      <Section title="Enrollment-Based Pay">
        <p className="text-sm text-muted-foreground mb-3">
          Pay that grows with paid enrollment. Each meeting is priced from the paid enrollment on
          that meeting's date, so already-taught meetings never change.
        </p>
        {tieredOverrides.length === 0 ? (
          <p className="text-sm text-muted-foreground">None configured.</p>
        ) : (
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {tieredOverrides.map((o) => (
              <li key={o.id} className="px-4 py-3">
                <p className="text-[15px] font-medium text-foreground truncate">
                  {nameFor(o.instructor_id)}
                  {o.class_slug && <span className="text-muted-foreground"> · {o.class_slug}</span>}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {money(o.rate_cents)} per meeting up to {o.tier_threshold_students ?? 0} paid
                  students, then +{money(o.tier_increment_cents)} for each additional paid student
                </p>
                {o.effective_start_date && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Effective {o.effective_start_date}
                    {o.effective_end_date ? ` – ${o.effective_end_date}` : ""}
                  </p>
                )}
                {o.notes && <p className="text-xs text-muted-foreground italic mt-1">{o.notes}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Standard-Schedule Overrides">
        {standardOverrides.filter((o) => o.compensation_source !== "volunteer").length === 0 ? (
          <p className="text-sm text-muted-foreground">No custom rates on the standard schedule.</p>
        ) : (
          <ul className="rounded-lg border border-border/60 bg-card divide-y divide-border/50 overflow-hidden">
            {standardOverrides
              .filter((o) => o.compensation_source !== "volunteer")
              .map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-foreground truncate">
                      {nameFor(o.instructor_id)}
                      {o.class_slug && <span className="text-muted-foreground"> · {o.class_slug}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {o.compensation_source}
                      {o.rate_type && <> · {o.rate_type}</>}
                    </p>
                    {o.notes && <p className="text-xs text-muted-foreground italic mt-1">{o.notes}</p>}
                  </div>
                  <span className="text-sm font-medium text-foreground shrink-0">
                    {money(o.rate_cents)}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </Section>

      <p className="mt-6 text-xs text-muted-foreground">
        To add or change an override, use the instructor's Personnel File. Historical payroll
        remains immutable — edits here only affect future pay runs.
      </p>
    </ExecutivePage>
  );
}
